// DS2 — 현장 카드(시안 base .card · hub 메타 분리): ds3 3:2 이미지(라운드 10) + 좌상단 흰 상태 칩 → 이름 14/700 → 메타 12 sub 1줄.
// 카드 전체 링크 = 제목 <a> 의 ::after. 카드 루트에 data-ds2-card. 그림자·이중 테두리·카드 안 카드 없음.
// 이미지 선택(라이선스·생성 카드 배제·공식/생성 판정)은 호출부 책임 — 받은 src 를 그린다.
// ui1(2026-10-17) — 이미지가 없으면 «컴팩트 텍스트 카드»(data-noimg · 3:2 빈 박스·남색 그라데이션 자리표시 금지). 깨진 이미지도 CardImg 가 같은 모양으로 접는다.
import type { ReactNode } from 'react';
import { clean, MetaSpans, SR_ONLY, type MetaItem } from './meta';
import CardImg from './CardImg';
import './ds2.css';

export type Ds2BadgeTone = 'accent' | 'neutral' | 'muted';
export interface SiteCardBadge { label: string; tone?: Ds2BadgeTone }

export interface SiteCardProps {
  title: string;
  href?: string | null;
  thumbSrc?: string | null;
  /** 이미지 alt. 생략 시 ''(제목이 바로 옆). */
  thumbAlt?: string | null;
  /** 생성 이미지 고지 문구 — 있으면 figure + 시각 숨김 figcaption + img title. */
  thumbCaption?: string | null;
  /** 이미지 우하단 「AI 상상도」 칩. 공식 이미지(트라비스 등)면 넘기지 않는다. */
  thumbLabel?: string | null;
  /** vx_scale1 E · v2 §J — 이미지 바로 아래 사실 배지(「3개동 · 최고 15층 · 총 N세대」 · 12px). 이미지가 있을 때만. */
  thumbFacts?: string | null;
  /** 썸네일 칸 안을 통째로 대체(예: 기존 SiteThumb). */
  thumb?: ReactNode;
  /** 좌상단 흰 상태 칩(예: 「분양예정」 · 청약 카드는 「10/26 (월) · D-9」). 이미지 없는 카드에서는 제목 위 줄에 선다. */
  status?: string | null;
  /** ui1 — 상태 칩 톤. 'hot' = D-1 이하·오늘 마감(주황 #C2410C · DdayPill 과 같은 값). */
  statusTone?: 'hot' | null;
  /** 메타 1줄 — 항목마다 span, 사이 « · ». { text, late: true } 는 주황 700(「일정 지연」). */
  meta?: MetaItem[];
  /** ds4 — 이름 위 시기 라벨(12/700 네이비, 예: 「2026년 10월 예정」). */
  periodLabel?: string | null;
  /** ds4 — 시기 라벨 톤. 'delay' = 주황 700(「일정 지연」, salePeriodBadge tone). 기본 네이비. */
  periodTone?: 'navy' | 'delay' | null;
  /** 메타 아래 12 mute 한 줄(예: 「당초 2025년 3월 예정」). */
  sub?: string | null;
  /** 제목 태그. 기본 h3. */
  as?: 'h3' | 'h4' | 'p';
  priority?: boolean;
  className?: string;
  dataAttrs?: Record<`data-${string}`, string>;
  /** @deprecated 시안에 없음 — status 를 쓴다. 넘기면 status 로 취급. */
  badge?: string | SiteCardBadge | null;
}

export function Ds2Badge({ label }: SiteCardBadge) {
  return <span className="ds2-chip">{label}</span>;
}

export default function SiteCard({ title, href, thumbSrc, thumbAlt, thumbCaption, thumbLabel, thumbFacts, thumb, status, statusTone, meta, sub, periodLabel, periodTone, as = 'h3', priority, className, dataAttrs, badge }: SiteCardProps) {
  const T = as;
  const st = clean(status) ? String(status).trim() : badge ? (typeof badge === 'string' ? badge : badge.label) : '';
  const src = clean(thumbSrc) ? String(thumbSrc) : '';
  const alt = clean(thumbAlt) ? String(thumbAlt).trim() : '';
  const cap = clean(thumbCaption) ? String(thumbCaption).trim() : '';
  const lb = clean(thumbLabel) ? String(thumbLabel).trim() : '';
  const img = src ? <CardImg src={src} alt={alt} title={cap || undefined} width={420} height={280} loading={priority ? 'eager' : 'lazy'} /> : null;
  const overlays = (
    <>
      {st ? <span className="ds2-card__st" {...(statusTone === 'hot' ? { 'data-hot': '' } : {})}>{st}</span> : null}
      {lb && (img || thumb) ? <span className="ds2-card__ai">{lb}</span> : null}
    </>
  );
  const metaNode = MetaSpans({ items: meta });
  const noImg = !img && !thumb;
  return (
    <article className={className ? `ds2-card ${className}` : 'ds2-card'} data-ds2-card="site" {...(dataAttrs ?? {})} {...(noImg ? { 'data-noimg': '' } : {})}>
      {thumb ? (
        <div className="ds2-card__img">{thumb}{overlays}</div>
      ) : cap && img ? (
        <figure className="ds2-card__img" data-thumb="generated">
          {img}
          <figcaption style={SR_ONLY}>{cap}</figcaption>
          {overlays}
        </figure>
      ) : img ? (
        <div className="ds2-card__img">{img}{overlays}</div>
      ) : st ? (
        <div className="ds2-card__img">{overlays}</div>
      ) : null}
      {clean(thumbFacts) && (img || thumb) ? <p className="ds2-card__fx" data-scale-badge="">{String(thumbFacts).trim()}</p> : null}
      {clean(periodLabel) ? <p className="ds2-card__pl" data-period-label="" {...(periodTone === 'delay' ? { 'data-period-tone': 'delay' } : {})}>{String(periodLabel).trim()}</p> : null}
      <T className="ds2-card__t">{href ? <a href={href}>{title}</a> : title}</T>
      {metaNode ? <p className="ds2-card__meta">{metaNode}</p> : null}
      {clean(sub) ? <p className="ds2-card__sub">{sub}</p> : null}
    </article>
  );
}
